/* SEMU Popup
   Gecikmeli açılış + "kapatınca N gün gösterme" mantığı. */

class SEMUPopup {
  constructor(element) {
    this.element = element;
    this.dialog = element.querySelector('[data-semu-popup-dialog]');
    this.overlay = element.querySelector('[data-semu-popup-overlay]');
    this.closeButton = element.querySelector('[data-semu-popup-close]');
    this.link = element.querySelector('[data-semu-popup-link]');

    this.sectionId = element.dataset.sectionId;
    this.storageKey = `semu-popup-dismissed-${this.sectionId}`;
    this.delay = parseInt(element.dataset.delay, 10) || 0;
    this.frequencyDays = parseFloat(element.dataset.frequencyDays) || 0;
    this.closeOnOverlay = element.dataset.closeOnOverlay === 'true';
    this.closeOnEsc = element.dataset.closeOnEsc === 'true';
    this.showOnMobile = element.dataset.showOnMobile !== 'false';
    this.showOnDesktop = element.dataset.showOnDesktop !== 'false';
    this.mobileBreakpoint = 750;

    this.isOpen = false;
    this.timer = null;
    this.lastFocused = null;
    this.designMode = Boolean(window.Shopify && window.Shopify.designMode);

    this.onKeydown = this.onKeydown.bind(this);

    this.init();
  }

  init() {
    if (!this.dialog) return;
    if (!this.matchesViewport()) return;
    // Tema editöründe her zaman göster, kayıt tutma.
    if (!this.designMode && this.isDismissed()) return;

    this.bindEvents();
    this.preloadImage();

    this.timer = setTimeout(() => this.open(), this.delay);
  }

  // Popup gerçekten gösterilecekse görseli baştan indir; aksi halde
  // ziyaretçi boşuna byte harcamasın.
  preloadImage() {
    const image = this.element.querySelector('.semu-popup__image');
    if (image && image.loading === 'lazy') image.loading = 'eager';
  }

  matchesViewport() {
    const isMobile = window.matchMedia(`(max-width: ${this.mobileBreakpoint - 1}px)`).matches;
    return isMobile ? this.showOnMobile : this.showOnDesktop;
  }

  bindEvents() {
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    if (this.overlay && this.closeOnOverlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    if (this.link) {
      // Linke tıklandığında da kapatılmış sayılır.
      this.link.addEventListener('click', () => this.remember());
    }
  }

  open() {
    if (this.isOpen) return;

    this.lastFocused = document.activeElement;
    this.isOpen = true;
    this.element.classList.add('is-visible');
    this.element.removeAttribute('aria-hidden');

    // Geçiş animasyonunun tetiklenmesi için bir frame bekle.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.element.classList.add('is-open'));
    });

    document.body.classList.add('semu-popup-open');
    document.addEventListener('keydown', this.onKeydown);

    if (this.closeButton) {
      this.closeButton.focus({ preventScroll: true });
    }
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.element.classList.remove('is-open');
    document.body.classList.remove('semu-popup-open');
    document.removeEventListener('keydown', this.onKeydown);

    setTimeout(() => {
      this.element.classList.remove('is-visible');
      this.element.setAttribute('aria-hidden', 'true');
    }, 400);

    this.remember();

    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus({ preventScroll: true });
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.closeOnEsc) {
      this.close();
      return;
    }

    // Basit focus trap: popup açıkken odak dışarı çıkmasın.
    if (event.key === 'Tab') {
      const focusable = this.element.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  isDismissed() {
    // 0 gün = her ziyarette göster.
    if (this.frequencyDays <= 0) return false;

    const stored = this.readStorage();
    if (!stored) return false;

    const dismissedAt = parseInt(stored, 10);
    if (!dismissedAt) return false;

    const expiresAt = dismissedAt + this.frequencyDays * 24 * 60 * 60 * 1000;
    return Date.now() < expiresAt;
  }

  remember() {
    if (this.designMode) return;
    if (this.frequencyDays <= 0) return;
    this.writeStorage(String(Date.now()));
  }

  readStorage() {
    try {
      return window.localStorage.getItem(this.storageKey);
    } catch (e) {
      return this.readCookie();
    }
  }

  writeStorage(value) {
    try {
      window.localStorage.setItem(this.storageKey, value);
    } catch (e) {
      this.writeCookie(value);
    }
  }

  readCookie() {
    const match = document.cookie.match(new RegExp(`(?:^|; )${this.storageKey}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  }

  writeCookie(value) {
    const maxAge = Math.round(this.frequencyDays * 24 * 60 * 60);
    document.cookie = `${this.storageKey}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
  }

  destroy() {
    clearTimeout(this.timer);
    document.removeEventListener('keydown', this.onKeydown);
    document.body.classList.remove('semu-popup-open');
  }
}

function initSemuPopups(root) {
  (root || document).querySelectorAll('[data-semu-popup]').forEach((element) => {
    if (element.semuPopup) element.semuPopup.destroy();
    element.semuPopup = new SEMUPopup(element);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initSemuPopups());
} else {
  initSemuPopups();
}

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('[data-semu-popup]')) initSemuPopups(event.target);
  });

  document.addEventListener('shopify:section:select', (event) => {
    const popup = event.target.querySelector('[data-semu-popup]');
    if (popup && popup.semuPopup) popup.semuPopup.open();
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const popup = event.target.querySelector('[data-semu-popup]');
    if (popup && popup.semuPopup) popup.semuPopup.close();
  });
}
